/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';

import { getUrl } from '@coursera-web/cds-labs';

import { simplifyQueryValue } from 'js/lib/queryUtils';
import useRouter, { useLocation } from 'js/lib/useRouter';

import { Accordion, AccordionGroup, AccordionHeader, AccordionPanel, Typography } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import type { EntWebsiteBlockFaqQuery, EntWebsiteFaqFragment } from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout, { noImage } from 'bundles/ent-website/contentful/core/BlockLayout';
import Markdown from 'bundles/ent-website/contentful/core/Markdown';
import { entWebsiteBlockFAQQuery } from 'bundles/ent-website/queries/EntWebsiteFAQ';
import DataConsumerWrapper from 'bundles/ent-website/utils/DataConsumerWrapper';
import { getPromoCodeSubstMap, runSubstituitonsForString } from 'bundles/ent-website/utils/promoCodeUtils';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import BrowseEntityFAQSchemaMarkup from 'bundles/seo/components/BrowseEntityFAQSchemaMarkup';

type ComponentProps<T> = T extends React.Component<infer P> ? P : never;
export type Props = BlockModuleInnerProps<EntWebsiteFaqFragment>;

export function FAQModule({ content, assembly }: Props) {
  const styles: Record<string, Interpolation<Theme>> = {
    accordionRoot: () => css`
      max-width: 888px;
      margin: auto;
      background: transparent !important;

      .cds-AccordionRoot-container {
        background: transparent !important;

        &::after {
          content: '';
          display: block;
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: var(--cds-color-grey-50);
        }
      }

      .cds-AccordionGroup-itemSpacing {
        margin-top: 0;
        padding-top: 8px;
        padding-bottom: 8px;
      }
    `,
    accordionHeader: css`
      /* stylelint-disable-line no-empty-source */
    `,
    title: css`
      text-align: center;
      margin-bottom: 1em;
    `,
    accordionPanel: css`
      padding-top: 1rem;
    `,
  };

  const router = useRouter();
  const location = useLocation();

  const { visibility } = assembly;
  const { questionsCollection } = content;

  const promoCodeSubstMap: Record<string, string> = React.useMemo(
    () =>
      getPromoCodeSubstMap(content?.promoCodeConfigsCollection?.items, simplifyQueryValue(location?.query?.promoCode)),
    [content?.promoCodeConfigsCollection?.items, location?.query?.promoCode]
  );
  const title = runSubstituitonsForString(content?.title, promoCodeSubstMap);

  const questions = questionsCollection?.items ?? [];
  const faqs = questions.map((faq) => ({
    question: faq?.question ?? '',
    answer: faq?.answer ?? '',
  }));

  const renderers = {
    link: (props: ComponentProps<TrackedA>) => {
      const { href, trackingName } = props;
      const modifiedHref = getUrl(router, href);
      return <TrackedA {...props} trackingName={trackingName || 'link_in_contentful'} href={modifiedHref} />;
    },
  };

  const contentPanel = () => (
    <div>
      {visibility?.title && title && (
        <Typography color="inherit" variant="d2semibold" component="h2" css={styles.title}>
          {title}
        </Typography>
      )}

      <AccordionGroup css={styles.accordionRoot}>
        {questions.map((faq) => {
          const faqPromoCodeSubstMap = getPromoCodeSubstMap(
            faq?.promoCodeConfigsCollection?.items,
            simplifyQueryValue(location?.query?.promoCode)
          );

          return (
            <Accordion variant="silent" key={faq?.sys?.id}>
              <AccordionHeader
                label={runSubstituitonsForString(faq?.question ?? '', faqPromoCodeSubstMap) ?? ''}
                css={styles.accordionHeader}
              />
              <DataConsumerWrapper template={runSubstituitonsForString(faq?.answer ?? '', faqPromoCodeSubstMap)}>
                {({ result, isLoading }) => (
                  <AccordionPanel css={styles.accordionPanel}>
                    <Markdown source={isLoading ? 'Loading...' : result} renderers={renderers} module={content} />
                  </AccordionPanel>
                )}
              </DataConsumerWrapper>
            </Accordion>
          );
        })}
      </AccordionGroup>

      <BrowseEntityFAQSchemaMarkup faqs={faqs} />
    </div>
  );

  return <BlockLayout assembly={noImage(assembly)} renderContent={contentPanel} />;
}

// @ts-expect-error TSMIGRATION-5.0 TS2344
export const BlockFAQModule = withBlockQuery<EntWebsiteFaqFragment, EntWebsiteBlockFaqQuery>(entWebsiteBlockFAQQuery)(
  FAQModule
);
